/**
 * Utilities for "Manage Test Data" (onboard / project-level) flow.
 *
 * This is extracted from the legacy `Hosting` container class methods so it can be reused
 * by the new full-page experience without duplicating logic.
 */

/**
 * Upload test data files to S3 and update the payload with returned URIs (new testdata format).
 *
 * @param {Object} args
 * @param {Object<string, Object>} args.payload - Test cases payload keyed by testCaseId
 * @param {Object<string, Object>} args.files - Uploaded files keyed by testCaseId and envKey
 * @param {string} args.workstream - Workstream name
 * @param {string} args.project - Project name
 * @param {Array<Object>} args.originalTestCases - Original test cases fetched from API
 * @param {Function} args.endpoint - API method used to upload a file (onboard or execution variant)
 * @returns {Promise<{payload: Object, filesToRemoveFromS3: string[]}>}
 */
export async function uploadFilesToS3AndUpdatePayloadForNewTestDataFormat({
  payload,
  files,
  workstream,
  project,
  originalTestCases,
  endpoint,
}) {
  const fileUploadRequests = [];
  const testCaseIDMap = new Map();
  const filesToRemoveFromS3 = [];

  // prepare requests to upload files to S3
  Object.entries(files || {}).forEach(([testCaseId, envFiles]) => {
    const testcase = payload?.[testCaseId]?.testcasename;
    if (!testcase) return;

    testCaseIDMap.set(testcase, testCaseId);

    Object.entries(envFiles || {}).forEach(([env, rows]) => {
      Object.entries(rows || {}).forEach(([rowIndex, file]) => {
        const fileType = Object.keys(file || {})[0];
        const rowName = fileType === 'testdatafile' ? rows[rowIndex]?._name : env;

        if (rows?.[rowIndex]?.[fileType] instanceof File) {
          const isExecutionEndpoint =
            endpoint?.name === 'postHostingCreateExecutionTestDataFile' ||
            endpoint?.toString?.().includes('postHostingCreateExecutionTestDataFile');

          if (isExecutionEndpoint) {
            // execution endpoint: (workstream, project, testcase, payload, env, filetype)
            fileUploadRequests.push(
              endpoint(
                workstream,
                project,
                testcase,
                rows[rowIndex][fileType],
                env,
                fileType
              ).then((response) => ({ response, rowIndex, fileType }))
            );
          } else {
            // onboard endpoint: (workstream, project, testcase, rowname, payload, env, filetype)
            fileUploadRequests.push(
              endpoint(
                workstream,
                project,
                testcase,
                rowName,
                rows[rowIndex][fileType],
                env,
                fileType
              ).then((response) => ({ response, rowIndex, fileType }))
            );
          }
        }
      });
    });
  });

  // Check for files to remove from S3
  for (const testCaseId in files) {
    const originalTestCase = (originalTestCases || []).find(
      (tc) => tc.id === testCaseId
    );
    if (!originalTestCase) continue;

    for (const envKey in files[testCaseId]) {
      const oldEnvKey = envKey.split('_')[1]?.toUpperCase?.();
      for (const fileKey in files[testCaseId][envKey]) {
        const file = files[testCaseId][envKey][fileKey];

        // new-format testdatafile removed from a specific row
        if (
          file?.testdatafile === '' &&
          originalTestCase?.[envKey]?.[fileKey]?.testdatafile
        ) {
          filesToRemoveFromS3.push(originalTestCase[envKey][fileKey].testdatafile);
        }

        // iteration file removed
        if (fileKey === 'testiterationfile' && !file?.testiterationfile) {
          // old format: { ENV: { ... , testiterationfile } }
          if (
            oldEnvKey &&
            originalTestCase?.testdata?.[oldEnvKey] &&
            originalTestCase.testdata[oldEnvKey].testiterationfile
          ) {
            filesToRemoveFromS3.push(originalTestCase.testdata[oldEnvKey].testiterationfile);

            // also clear iteration file reference from old-format payload
            const rest = Object.entries(originalTestCase.testdata[oldEnvKey]).reduce(
              (acc, [k, v]) => {
                if (k !== 'testiterationfile') acc[k] = v;
                return acc;
              },
              {}
            );
            if (payload?.[testCaseId]) {
              payload[testCaseId].testdata = {
                ...originalTestCase.testdata,
                [oldEnvKey]: { ...rest },
              };
            }
          } else if (originalTestCase?.[envKey]) {
            // new format: array rows; iteration file lives on a row
            const originalIterationFile = originalTestCase[envKey].find(
              (testDataRow) => testDataRow?.testiterationfile
            );
            if (originalIterationFile?.testiterationfile) {
              filesToRemoveFromS3.push(originalIterationFile.testiterationfile);
            }
          }
        }
      }
    }
  }

  // Upload test data files to S3
  const responses = await Promise.all(fileUploadRequests);

  // update payload with returned URIs
  responses.forEach(({ response, fileType, rowIndex }) => {
    const fileLocation = response?.data?.file_location;
    const testCaseName = response?.data?.testcasename;
    const testdataEnvKey = response?.data?.environment;
    const testCaseId = testCaseIDMap.get(testCaseName);

    if (!fileLocation || !testCaseId || !testdataEnvKey) return;

    if (payload?.[testCaseId]?.[testdataEnvKey]) {
      if (fileType === 'testdatafile') {
        payload[testCaseId][testdataEnvKey][rowIndex][fileType] = fileLocation;
      } else {
        payload[testCaseId][testdataEnvKey][0] = {
          ...payload[testCaseId][testdataEnvKey][0],
          [fileType]: fileLocation,
        };
      }
    }
  });

  return { payload, filesToRemoveFromS3 };
}

